import { useState, type ReactElement } from "react";
import { Plus } from "lucide-react";
import {
  type ControlPlanRow,
  type ControlPlanParams,
} from "@playbookai/widget-schemas";
import { Button } from "../../atoms/index.js";
import type { WidgetBodyProps } from "../types.js";
import { ControlList } from "./Component.js";
import { ControlDetails } from "./ControlDetails.js";
const MAX_CONTROLS = 50;

export function ControlPlanExpandedEditor({
  data,
  onChange,
  readOnly,
}: WidgetBodyProps<ControlPlanParams>): ReactElement {
  const [selectedId, setSelectedId] = useState<string>();
  const [newRow, setNewRow] = useState<ControlPlanRow>();
  const [dirty, setDirty] = useState(false);
  const [revision, setRevision] = useState(0);
  // Retain selection identity if removed remotely, never edit the next row by accident.
  const selected = selectedId
    ? data.rows.find((row) => row.id === selectedId)
    : data.rows[0];
  const reset = () => {
    setDirty(false);
    setNewRow(undefined);
    setRevision((value) => value + 1);
  };
  const add = () => {
    if (readOnly || dirty || newRow || data.rows.length >= MAX_CONTROLS) return;
    setNewRow({
      id: crypto.randomUUID(),
      item: "",
      responsible: "",
      cadence: "",
      trigger: "",
    });
  };
  return (
    <div className="@container flex min-h-0 flex-1 flex-col gap-4 overflow-auto">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-pb-text-muted">
          {dirty || newRow
            ? "Save or cancel before selecting another control."
            : "Select a control"}
        </p>
        {!readOnly && (
          <Button
            size="sm"
            variant="outline"
            onClick={add}
            disabled={dirty || !!newRow || data.rows.length >= MAX_CONTROLS}
          >
            <Plus className="me-2 h-4 w-4" />
            Add control
          </Button>
        )}
      </div>
      <div className="grid shrink-0 gap-5 @3xl:min-h-0 @3xl:flex-1 @3xl:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
        <div className="flex min-w-0 flex-col @3xl:min-h-0">
          <ControlList
            data={data}
            selectedId={newRow ? undefined : selected?.id}
            locked={dirty || !!newRow}
            onSelect={(id) => {
              setSelectedId(id);
              reset();
            }}
          />
          <p className="mt-4 text-xs text-pb-text-muted">
            {data.rows.length} control{data.rows.length === 1 ? "" : "s"}
          </p>
        </div>
        <div className="min-w-0 border-t border-pb-border pt-4 @3xl:overflow-auto @3xl:border-s @3xl:border-t-0 @3xl:ps-5 @3xl:pt-0">
          {newRow || selected ? (
            <ControlDetails
              key={(newRow?.id ?? selected?.id) + ":" + revision}
              row={(newRow ?? selected)!}
              data={data}
              isNew={!!newRow}
              readOnly={!!readOnly}
              onDirty={() => {
                if (selected) setSelectedId(selected.id);
                setDirty(true);
              }}
              onCancel={() => {
                reset();
                if (!selected) setSelectedId(undefined);
              }}
              onSave={(row, options) => {
                const next = {
                  ...data,
                  rows: newRow
                    ? [...data.rows, row]
                    : data.rows.map((item) =>
                        item.id === row.id ? row : item,
                      ),
                };
                const saved = options
                  ? onChange(next, options)
                  : onChange(next);
                const done = () => {
                  setSelectedId(row.id);
                  reset();
                };
                // A pick's save is awaited: a refusal keeps the draft open.
                if (options?.grantViewerIfMissing) {
                  return Promise.resolve(saved).then(done);
                }
                done();
                return undefined;
              }}
              onDelete={() => {
                if (readOnly) return;
                onChange({
                  ...data,
                  rows: data.rows.filter((row) => row.id !== selected?.id),
                });
                setSelectedId(undefined);
                reset();
              }}
            />
          ) : (
            <div className="space-y-3 text-sm text-pb-text-muted">
              <p>
                {selectedId
                  ? "This control was removed. Select another or add one."
                  : "Add a control to begin."}
              </p>
              {dirty && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    reset();
                    setSelectedId(undefined);
                  }}
                >
                  Cancel
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
